// MainContentArea.js - Redesigned middle section wrapper
// Matches the sidebar aesthetic with refined slate/indigo design language
import React from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

/**
 * MainContentArea - Wrapper component for the middle section
 * Provides consistent styling that matches the sidebar design language
 */

// Color palette matching sidebar - exported for reuse
export const getMainContentColors = (isDarkMode) => ({
  // Backgrounds
  background: isDarkMode ? "#0f172a" : "#f1f5f9",
  cardBg: isDarkMode ? "#1e293b" : "#ffffff",
  surfaceElevated: isDarkMode ? "#1e293b" : "#ffffff",

  // Borders
  border: isDarkMode ? "rgba(148, 163, 184, 0.1)" : "rgba(148, 163, 184, 0.15)",
  borderStrong: isDarkMode ? "rgba(148, 163, 184, 0.2)" : "rgba(148, 163, 184, 0.25)",

  // Accent colors (matching sidebar)
  accentPrimary: isDarkMode ? "#818cf8" : "#6366f1",
  accentSecondary: isDarkMode ? "#6366f1" : "#818cf8",
  accentGlow: isDarkMode ? "rgba(129, 140, 248, 0.15)" : "rgba(99, 102, 241, 0.1)",

  // Text colors
  textPrimary: isDarkMode ? "#f1f5f9" : "#0f172a",
  textSecondary: isDarkMode ? "#94a3b8" : "#64748b",
  textMuted: isDarkMode ? "#64748b" : "#94a3b8",
  textAccent: isDarkMode ? "#818cf8" : "#6366f1",

  // Shadows
  shadow: isDarkMode ? "#000" : "#64748b",
  shadowOpacity: isDarkMode ? 0.4 : 0.08,

  // Glossary kind accents (AIT-251) — keep in sync with i18n/glossary.js
  // Quiz: blue family
  quizAccent: isDarkMode ? "#60a5fa" : "#3B82F6",
  quizAccentBg: isDarkMode ? "rgba(96, 165, 250, 0.15)" : "rgba(59, 130, 246, 0.1)",
  // Test: purple family
  testAccent: isDarkMode ? "#a78bfa" : "#8B5CF6",
  testAccentBg: isDarkMode ? "rgba(167, 139, 250, 0.15)" : "rgba(139, 92, 246, 0.1)",
  // Homework Help: amber family
  homeworkAccent: isDarkMode ? "#fbbf24" : "#f59e0b",
  homeworkAccentBg: isDarkMode ? "rgba(251, 191, 36, 0.15)" : "rgba(245, 158, 11, 0.1)",
});

/**
 * Section Header component for content areas
 */
export const SectionHeader = ({
  title,
  subtitle,
  icon,
  isDarkMode,
  rightAction,
  onRightActionPress,
  rightActionIcon,
}) => {
  const colors = getMainContentColors(isDarkMode);

  return (
    <View style={[headerStyles.container, { borderBottomColor: colors.border }]}>
      <View style={headerStyles.left}>
        {icon && (
          <View style={[headerStyles.iconContainer, { backgroundColor: colors.accentGlow }]}>
            <MaterialCommunityIcons
              name={icon}
              size={18}
              color={colors.accentPrimary}
            />
          </View>
        )}
        <View style={headerStyles.textContainer}>
          <Text style={[headerStyles.title, { color: colors.textPrimary }]}>
            {title}
          </Text>
          {subtitle && (
            <Text style={[headerStyles.subtitle, { color: colors.textSecondary }]}>
              {subtitle}
            </Text>
          )}
        </View>
      </View>

      {rightAction && (
        <TouchableOpacity
          onPress={onRightActionPress}
          style={[headerStyles.actionButton, { backgroundColor: colors.accentGlow }]}
        >
          {rightActionIcon && (
            <MaterialCommunityIcons
              name={rightActionIcon}
              size={16}
              color={colors.accentPrimary}
              style={{ marginRight: 6 }}
            />
          )}
          <Text style={[headerStyles.actionText, { color: colors.accentPrimary }]}>
            {rightAction}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

/**
 * Content Card wrapper for elevated sections
 */
export const ContentCard = ({ children, isDarkMode, style, noPadding }) => {
  const colors = getMainContentColors(isDarkMode);

  return (
    <View
      style={[
        cardStyles.container,
        {
          backgroundColor: colors.cardBg,
          borderColor: colors.border,
          shadowColor: colors.shadow,
          shadowOpacity: colors.shadowOpacity,
        },
        noPadding && { padding: 0 },
        style,
      ]}
    >
      {children}
    </View>
  );
};

/**
 * Topic Grid wrapper for the topic cards
 */
export const TopicGrid = ({ children, isDarkMode }) => {
  return (
    <View style={gridStyles.container}>
      {children}
    </View>
  );
};

/**
 * Empty State component
 */
export const EmptyState = ({ icon, title, message, isDarkMode, action, onActionPress }) => {
  const colors = getMainContentColors(isDarkMode);

  return (
    <View style={emptyStyles.container}>
      <View style={[emptyStyles.iconContainer, { backgroundColor: colors.accentGlow }]}>
        <MaterialCommunityIcons
          name={icon || "folder-open-outline"}
          size={48}
          color={colors.accentPrimary}
        />
      </View>
      <Text style={[emptyStyles.title, { color: colors.textPrimary }]}>
        {title}
      </Text>
      {message && (
        <Text style={[emptyStyles.message, { color: colors.textSecondary }]}>
          {message}
        </Text>
      )}
      {action && (
        <TouchableOpacity
          onPress={onActionPress}
          style={[emptyStyles.actionButton, { backgroundColor: colors.accentPrimary }]}
        >
          <Text style={emptyStyles.actionButtonText}>{action}</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

/**
 * Chip/Tag component for categories
 */
export const Chip = ({ label, icon, isDarkMode, isActive, onPress }) => {
  const colors = getMainContentColors(isDarkMode);

  return (
    <TouchableOpacity
      onPress={onPress}
      style={[
        chipStyles.container,
        {
          backgroundColor: isActive ? colors.accentPrimary : colors.accentGlow,
          borderColor: isActive ? colors.accentPrimary : colors.border,
        },
      ]}
    >
      {icon && (
        <MaterialCommunityIcons
          name={icon}
          size={14}
          color={isActive ? "#fff" : colors.accentPrimary}
          style={{ marginRight: 4 }}
        />
      )}
      <Text
        style={[
          chipStyles.label,
          { color: isActive ? "#fff" : colors.textSecondary },
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

/**
 * Main Content Area wrapper component
 */
const MainContentArea = ({
  children,
  isDarkMode,
  header,
  showScrollView = true,
  contentContainerStyle,
  style,
}) => {
  const colors = getMainContentColors(isDarkMode);

  const content = (
    <View style={[mainStyles.inner, style]}>
      {header}
      {children}
    </View>
  );

  return (
    <View style={[mainStyles.container, { backgroundColor: colors.background }]}>
      {/* Subtle gradient overlay for depth */}
      <View
        style={[
          mainStyles.gradientOverlay,
          {
            background: isDarkMode
              ? "linear-gradient(180deg, rgba(15, 23, 42, 0.8) 0%, rgba(15, 23, 42, 0) 100%)"
              : "linear-gradient(180deg, rgba(248, 250, 252, 0.9) 0%, rgba(248, 250, 252, 0) 100%)",
          },
        ]}
        pointerEvents="none"
      />

      {showScrollView ? (
        <ScrollView
          style={mainStyles.scrollView}
          contentContainerStyle={[mainStyles.scrollContent, contentContainerStyle]}
          showsVerticalScrollIndicator={false}
        >
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </View>
  );
};

// Styles
const mainStyles = StyleSheet.create({
  container: {
    flex: 1,
    position: "relative",
  },
  gradientOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 100,
    zIndex: 0,
    pointerEvents: "none",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 80,
  },
  inner: {
    flex: 1,
    padding: 16,
  },
});

const headerStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 4,
    marginBottom: 16,
    borderBottomWidth: 1,
  },
  left: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  iconContainer: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    letterSpacing: 0.3,
  },
  subtitle: {
    fontSize: 13,
    marginTop: 2,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
  },
  actionText: {
    fontSize: 13,
    fontWeight: "600",
  },
});

const cardStyles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    elevation: 2,
  },
});

const gridStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginHorizontal: -4,
  },
});

const emptyStyles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
    paddingHorizontal: 20,
  },
  iconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 8,
    textAlign: "center",
  },
  message: {
    fontSize: 14,
    textAlign: "center",
    maxWidth: 300,
    lineHeight: 20,
  },
  actionButton: {
    marginTop: 20,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  actionButtonText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
});

const chipStyles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    marginRight: 8,
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "500",
  },
});

export default MainContentArea;
